'use client'

import { useMemo } from 'react'

import { useInfiniteQuery } from '@tanstack/react-query'
import { plainToClass } from 'class-transformer'

import { CreationDetail } from '../../models'
import { getTaskList, getUserWorkList } from '../../services'

export const USER_WORK_LIST = 'USER_WORK_LIST' as const
export const USER_SHARED_LIST = 'USER_SHARED_LIST' as const

export const useUserWorkList = (isCurrentUser?: boolean) => {
  const query = useInfiniteQuery({
    queryKey: [USER_WORK_LIST, isCurrentUser],
    enabled: isCurrentUser,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchIntervalInBackground: false,
    queryFn: async ({ pageParam = 0 }) => {
      const res = await getTaskList({ page: pageParam, pageSize: 20 })
      // 转换数据，应用 @Transform 装饰器
      const transformedData = {
        ...res,
        list: res.list.map(task =>
          plainToClass(CreationDetail, {
            ...task,
            extra: task.data
          })
        )
      }
      return transformedData
    },
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (lastPage.list.length === 0) {
        return undefined
      }
      return lastPageParam + 1
    },
    initialPageParam: 0
  })

  const list = useMemo((): CreationDetail[] => {
    if (!query.data) return []
    return query.data.pages.reduce((acc: CreationDetail[], page: ListResponse<CreationDetail>) => {
      return [...acc, ...page.list]
    }, [] as CreationDetail[])
  }, [query.data])

  // 总数量
  const total = useMemo(() => {
    if (!query.data?.pages.length) return 0
    return query.data.pages[0].total
  }, [query.data])

  const hasMore = useMemo(() => {
    return query.hasNextPage && !query.isFetchingNextPage
  }, [query.hasNextPage, query.isFetchingNextPage])

  return {
    list,
    total,
    query,
    hasMore
  }
}

export const useUserSharedList = (nickname: string) => {
  const query = useInfiniteQuery({
    queryKey: [USER_SHARED_LIST, nickname],
    refetchOnMount: false,
    enabled: !!nickname,
    queryFn: async ({ pageParam = 0 }) => {
      const res = await getUserWorkList({ nickName: nickname, page: pageParam, pageSize: 20 })
      // 转换数据，应用 @Transform 装饰器
      const transformedData = {
        ...res,
        list: res.list.map(item => plainToClass(CreationDetail, item))
      }
      return transformedData
    },
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (lastPage.list.length === 0) {
        return undefined
      }
      return lastPageParam + 1
    },
    initialPageParam: 0
  })

  const list = useMemo(() => {
    if (!query.data) return []
    return query.data.pages.reduce((acc: CreationDetail[], page: ListResponse<CreationDetail>) => {
      return [...acc, ...page.list]
    }, [] as CreationDetail[])
  }, [query.data])

  const total = useMemo(() => {
    if (!query.data?.pages.length) return 0
    return query.data.pages[0].total
  }, [query.data])

  const hasMore = useMemo(() => {
    return query.hasNextPage && !query.isFetchingNextPage
  }, [query.hasNextPage, query.isFetchingNextPage])

  const loadMore = async () => {
    if (hasMore) {
      await query.fetchNextPage()
    }
    return hasMore
  }

  return {
    list,
    total,
    loadMore,
    hasMore,
    query
  }
}
